
import { Vue, Options } from "vue-class-component";
import { ellipsisString, formatByCountry } from "@/app/infrastructures/misc/Utils";
import router from "@/app/ui/router";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import { CargoV2Data } from "@/domain/entities/CargoV2";
import { CargoV2ListRequest } from "@/data/payload/api/CargoV2ApiRequest";
import { CargoSummaryInProcessRequest } from "@/data/payload/api/CargoApiRequest";
import { Pagination } from "@/domain/entities/Pagination";
import CargoV2FilterSection from "./components/cargo-v2-filter-section.vue";
import { AccountController } from "../../controllers/AccountController";

@Options({
  components: { CargoV2FilterSection }
})
export default class CargoV2BookingInProgress extends Vue {
  localFilter = new CargoV2ListRequest();

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  get inProgressList(): CargoV2Data[] {
    return CargoV2Controller.inProgressList.data;
  }

  get inProgressPagination() {
    return CargoV2Controller.inProgressPagination;
  }

  set inProgressPagination(p: Pagination) {
    CargoV2Controller.setInProgressPagination(p);
  }

  get isLoading(): boolean {
    return CargoV2Controller.isLoading;
  }

  get errorCause(): string {
    return CargoV2Controller.errorCause;
  }

  get cargoListAvailable(): boolean {
    return (
      !this.inProgressList.length &&
      !this.isLoading &&
      (this.inProgressPagination?.page ?? 1) === 1
    );
  }

  get columns() {
    return [
      {
        name: this.$t("cargo.labels.bookingNumber"),
        styleHead: "w-56 text-left whitespace-no-wrap align-top break-all",
        "custom-slot": true,
        slotName: "no_booking"
      },
      {
        name: this.$t("cargo.labels.originDestinationCities"),
        styleHead:
          "min-w-[12rem] w-40 text-left whitespace-nowrap align-middle",
        "custom-slot": true,
        slotName: "origin_destination"
      },
      {
        name: this.$t("cargo.v2.column.etd"),
        styleHead: "w-56 text-left",
        "custom-slot": true,
        slotName: "etd"
      },
      {
        name: this.$t("cargo.v2.column.totalPiecesGrossWeightShort"),
        styleHead: "w-36 text-left whitespace-no-wrap align-top",
        render: (item: CargoV2Data) => {
          const translatedText = this.$t(
            "cargo.v2.column.piecesGrossWeightValue",
            {
              pieces: item.cargoActualPiece,
              weight: formatByCountry(item.totalActualCargoGrossWeight, { type: "weight", country: this.countryCode })
            }
          );

          return `<div class="overflow-ellipsis text-left">${translatedText}</div>`;
        }
      },
      {
        name: this.$t("cargo.labels.lastUpdated"),
        styleHead: "w-56 text-left whitespace-no-wrap align-middle",
        render: (item: CargoV2Data) => {
          const date = item.updatedAt
            ? this.formatDate(item.updatedAt, "DD MMMM YYYY, HH:mm")
            : "-";
          const translateText = this.$t("cargo.v2.datetime.timezoneSuffix", {
            date
          });
          return item.updatedAt
            ? `<div class="flex flex-col text-sm text-gray-lp-300">
                  <div class="overflow-ellipsis text-left"> ${translateText} </div>
                  <div class="truncate text-gray-lp-600 w-32">
                    ${item.updatedBy || "-"}
                  </div>
                  <div> ${item.cargoHubName || "-"} </div>
                </div>`
            : `<div class="overflow-ellipsis text-left"> - </div>`;
        }
      }
    ];
  }

  displayBookingNo(item: CargoV2Data): string {
    const no = item.cargoNumber || item.bookingRequestId || "";
    return ellipsisString(no, 40) || "-";
  }

  formatDate(dateStr: string, format = "DD MMMM YYYY"): string {
    if (!dateStr) return "";
    return this.$moment(dateStr).format(format);
  }

  etdDayOffset(item: CargoV2Data): number {
    if (!item.departureDate || !item.arrivalDate) return 0;
    const dep = this.$moment(item.departureDate).startOf("day");
    const arr = this.$moment(item.arrivalDate).startOf("day");
    return Math.max(0, arr.diff(dep, "days"));
  }

  etdDayOffsetLabel(item: CargoV2Data): string {
    const days = this.etdDayOffset(item);
    if (days === 0) return "";
    return days === 1
      ? this.$t("cargo.v2.etd.dayOffsetSingular")
      : this.$t("cargo.v2.etd.dayOffsetPlural", { count: days });
  }

  fetchInterval: ReturnType<typeof setInterval> | null = null;

  startInterval(): void {
    this.fetchInterval = setInterval(() => {
      this.fetchList();
    }, FlagsPermissionCargo.config_interval_auto_refresh_cargo.getValue() * 1000);
  }

  stopInterval(): void {
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
      this.fetchInterval = null;
    }
  }

  mounted(): void {
    this.fetchLastIdCargo();
    this.startInterval();
  }

  unmounted(): void {
    this.stopInterval();
  }

  onSearch(value: string) {
    this.localFilter.search = value;

    const p = CargoV2Controller.pagination;
    CargoV2Controller.setPagination(new Pagination(1, p?.limit, p?.totalData));

    this.fetchList();
  }
  onFilterChange(payload: CargoV2ListRequest): void {
    this.localFilter = payload;
    const p = CargoV2Controller.inProgressPagination;
    CargoV2Controller.setInProgressPagination(
      new Pagination(1, p?.limit ?? 20, p?.totalData ?? 0)
    );
    this.fetchList();
    this.refreshSummaryInProcess();
  }

  refreshSummaryInProcess(): void {
    const startDate = this.localFilter.startDate
      ? new Date(this.localFilter.startDate).toLocaleDateString("fr-CA")
      : "";
    const endDate = this.localFilter.endDate
      ? new Date(this.localFilter.endDate).toLocaleDateString("fr-CA")
      : "";
    const requestParams = new CargoSummaryInProcessRequest({
      originCity: this.originCity,
      destinationCity: this.localFilter.destinationCity,
      destinationAirport: this.localFilter.destinationAirport,
      startDate,
      endDate,
      hubIds: this.localFilter.hubIds
    });
    CargoV2Controller.getCargoSummaryInProcess(requestParams);
  }

  async fetchList(): Promise<void> {
    await CargoV2Controller.fetchListInProgress(
      new CargoV2ListRequest({
        ...this.localFilter,
        originCity: this.originCity,
        page: CargoV2Controller.inProgressPagination?.page,
        limit: CargoV2Controller.inProgressPagination?.limit
      })
    );
  }

  get originCity() {
    return (
      this.localFilter.originCity ||
      AccountController.accountData.account_location.city_code
    );
  }

  lastIdCargo = 0;
  async fetchLastIdCargo() {
    const newLastIdCargo = await CargoV2Controller.fetchLastIdCargoRetryCargo(
      new CargoV2ListRequest({ type: "retry-cargo" })
    );
    if (this.lastIdCargo === 0 && newLastIdCargo !== 0) {
      this.lastIdCargo = newLastIdCargo;
    }
    if (this.lastIdCargo === 0 || newLastIdCargo === 0) return;
    if (this.lastIdCargo !== newLastIdCargo) {
      this.lastIdCargo = newLastIdCargo;
      await this.fetchList();
    }
  }

  onClickRow(item: CargoV2Data): void {
    router.push(
      `/cargo-v2/${encodeURIComponent(item.cargoNumber)}/info?rc_no=${item.id}`
    );
  }
}
